import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ChevronLeft, ChevronRight, X, RefreshCw, ChevronDown } from 'lucide-react';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const presets = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '14d', label: 'Last 14 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

const getPresetRange = (preset) => {
  const end = new Date();
  let start = new Date();

  switch (preset) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case '7d':
      start.setDate(end.getDate() - 7);
      break;
    case '14d':
      start.setDate(end.getDate() - 14);
      break;
    case '30d':
      start.setDate(end.getDate() - 30);
      break;
    case 'month':
      start = new Date(end.getFullYear(), end.getMonth(), 1);
      break;
    case 'quarter':
      const currentQuarter = Math.floor(end.getMonth() / 3);
      start = new Date(end.getFullYear(), currentQuarter * 3, 1);
      break;
    case 'year':
      start = new Date(end.getFullYear(), 0, 1);
      break;
    case 'all':
      start = new Date(2020, 0, 1); // Set to your app's launch date
      break;
    default:
      start.setDate(end.getDate() - 30);
  }

  return { start, end };
};

const formatDate = (date) => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateRange = (start, end) => {
  const startStr = formatDate(start);
  const endStr = formatDate(end);
  return `${startStr} - ${endStr}`;
};

const getDaysInMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

const getFirstDayOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};

const isSameDay = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const isWithinRange = (date, start, end) => {
  const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return dateTime >= startTime && dateTime <= endTime;
};

// Mobile Modal Component
const TimeRangeMobileModal = ({ isOpen, onClose, onApply, selectedPreset, setSelectedPreset, customStart, setCustomStart, customEnd, setCustomEnd, currentMonth, setCurrentMonth, selectingStart, setSelectingStart, compareEnabled, setCompareEnabled }) => {
  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (selectingStart) {
      setCustomStart(clickedDate);
      setCustomEnd(null);
      setSelectingStart(false);
      setSelectedPreset('custom');
    } else {
      if (clickedDate >= customStart) {
        setCustomEnd(clickedDate);
        setSelectingStart(true);
      } else {
        setCustomStart(clickedDate);
        setCustomEnd(null);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12 w-12" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isStart = customStart && isSameDay(date, customStart);
      const isEnd = customEnd && isSameDay(date, customEnd);
      const isInRange = customStart && customEnd && isWithinRange(date, customStart, customEnd);
      const isToday = isSameDay(date, new Date());
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={cn(
            "h-12 w-12 text-base rounded-xl transition-all duration-200 relative font-medium",
            isStart || isEnd ? "bg-primary text-primary-foreground font-bold border-2 border-primary shadow-lg scale-105 z-10" : "",
            isInRange && !isStart && !isEnd ? "bg-primary/10 text-primary dark:bg-primary/20 border border-primary/30" : "",
            !isStart && !isEnd && !isInRange ? "hover:bg-primary/5 text-foreground" : "",
            isToday && !isStart && !isEnd ? "ring-2 ring-primary/30" : "",
            "focus:outline-none focus:ring-2 focus:ring-primary/60"
          )}
          aria-label={`Select ${date.toDateString()}`}
        >
          {day}
        </button>
      );
    }
    return days;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] bg-background"
        >
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-background">
            <div>
              <h2 className="text-lg font-semibold">Date Range</h2>
              <p className="text-sm text-muted-foreground">Select your time period</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-10 w-10 p-0 rounded-xl"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-background">
            <div className="p-4 space-y-6 max-w-md mx-auto">
              {/* Presets */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold">Quick Presets</h3>
                <div className="grid grid-cols-2 gap-3">
                  {presets.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => handlePresetSelect(preset.value)}
                      className={cn(
                        "px-4 py-3 rounded-xl text-sm font-medium border transition-all",
                        selectedPreset === preset.value
                          ? "bg-primary text-primary-foreground border-primary shadow-lg"
                          : "bg-card border-border hover:border-primary/30 hover:bg-primary/5"
                      )}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calendar */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold">Custom Range</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigateMonth(-1)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      aria-label="Previous month"
                    >
                      <ChevronLeft className="h-5 w-5 text-muted-foreground" />
                    </button>
                    <h4 className="text-base font-semibold text-foreground min-w-[120px] text-center">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h4>
                    <button
                      onClick={() => navigateMonth(1)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      aria-label="Next month"
                    >
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </button>
                  </div>
                </div>

                {/* Day labels */}
                <div className="grid grid-cols-7 gap-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="h-12 flex items-center justify-center text-xs font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {renderCalendar()}
                </div>

                {/* Selection helper */}
                {(customStart || customEnd) && (
                  <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                    <div className="text-sm text-primary font-medium">
                      {customStart && !customEnd && 'Select end date'}
                      {customStart && customEnd && `Selected: ${formatDateRange(customStart, customEnd)}`}
                    </div>
                  </div>
                )}
              </div>

              {/* Compare option */}
              <div className="p-4 bg-card rounded-xl border border-border">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={compareEnabled}
                    onChange={(e) => setCompareEnabled(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Compare to previous period</span>
                </label>
              </div>
            </div>
          </div>

          {/* Sticky Footer with Actions */}
          <div className="sticky bottom-0 p-4 border-t border-border bg-background">
            <div className="flex gap-3 max-w-md mx-auto">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 h-12 text-base font-medium"
              >
                Cancel
              </Button>
              <Button
                onClick={onApply}
                className="flex-1 h-12 text-base font-medium bg-primary hover:bg-primary/90"
              >
                Apply
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const TimeRangeFilter = ({ isMobile = false, onMobileOpen }) => {
  const { timeRange, setTimeRange, isReloading, reloadData } = useTimeRange();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(timeRange);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [compareEnabled, setCompareEnabled] = useState(false);

  // Sync selectedPreset with timeRange from context
  useEffect(() => {
    setSelectedPreset(timeRange);
  }, [timeRange]);

  const { start: presetStart, end: presetEnd } = getPresetRange(selectedPreset);
  const currentStart = customStart || presetStart;
  const currentEnd = customEnd || presetEnd;

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (selectingStart) {
      setCustomStart(clickedDate);
      setCustomEnd(null);
      setSelectingStart(false);
      setSelectedPreset('custom');
    } else {
      if (clickedDate >= customStart) {
        setCustomEnd(clickedDate);
        setSelectingStart(true);
      } else {
        setCustomStart(clickedDate);
        setCustomEnd(null);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleApply = async () => {
    // Update the time range in context
    setTimeRange(selectedPreset);

    // Close the picker
    setIsOpen(false);

    // Trigger data reload
    await reloadData();
  };

  const handleReload = async () => {
    await reloadData();
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8 w-8" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isStart = customStart && isSameDay(date, customStart);
      const isEnd = customEnd && isSameDay(date, customEnd);
      const isInRange = customStart && customEnd && isWithinRange(date, customStart, customEnd);
      const isToday = isSameDay(date, new Date());
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={cn(
            "h-8 w-8 text-sm rounded-lg transition-all duration-200 relative",
            isStart || isEnd ? "bg-primary text-primary-foreground font-bold border-2 border-primary shadow-lg scale-105 z-10" : "",
            isInRange && !isStart && !isEnd ? "bg-primary/10 text-primary dark:bg-primary/20 border border-primary/30" : "",
            !isStart && !isEnd && !isInRange ? "hover:bg-primary/5 text-foreground" : "",
            isToday && !isStart && !isEnd ? "ring-2 ring-primary/30" : "",
            "focus:outline-none focus:ring-2 focus:ring-primary/60"
          )}
          aria-label={`Select ${date.toDateString()}`}
        >
          {day}
        </button>
      );
    }
    return days;
  };

  // Mobile icon-only version
  if (isMobile) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpen}
          className="h-10 w-10 p-0 text-muted-foreground hover:text-foreground hover:bg-background/40 rounded-xl relative"
        >
          <Calendar className="h-5 w-5" />
          {isReloading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <RefreshCw className="h-3 w-3 animate-spin text-primary" />
            </div>
          )}
        </Button>

        {/* Mobile Modal */}
        <TimeRangeMobileModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onApply={handleApply}
          selectedPreset={selectedPreset}
          setSelectedPreset={setSelectedPreset}
          customStart={customStart}
          setCustomStart={setCustomStart}
          customEnd={customEnd}
          setCustomEnd={setCustomEnd}
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          selectingStart={selectingStart}
          setSelectingStart={setSelectingStart}
          compareEnabled={compareEnabled}
          setCompareEnabled={setCompareEnabled}
        />
      </>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* Reload Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleReload}
        disabled={isReloading}
        className="h-8 w-8 p-0"
      >
        <RefreshCw className={cn("h-4 w-4", isReloading && "animate-spin")} />
      </Button>

      {/* Time Range Picker */}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-background border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow text-foreground text-xs sm:text-sm"
        >
          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
          <span className="font-medium truncate max-w-[120px] sm:max-w-none">
            {formatDateRange(currentStart, currentEnd)}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute right-0 mt-2 z-50 bg-background rounded-2xl shadow-2xl border border-border w-[420px] max-w-[95vw] p-4 flex flex-col gap-6"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-foreground">Select Date Range</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-muted rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>

              {/* Presets */}
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handlePresetSelect(preset.value)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm font-semibold border transition-all",
                      selectedPreset === preset.value
                        ? "bg-primary text-primary-foreground border-primary shadow"
                        : "bg-muted text-foreground border border-border hover:bg-primary/10 hover:text-primary"
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Calendar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="h-5 w-5 text-muted-foreground" />
                  </button>
                  <h4 className="text-base font-semibold text-foreground">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h4>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    aria-label="Next month"
                  >
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </button>
                </div>

                {/* Day labels */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {renderCalendar()}
                </div>
              </div>

              {/* Custom selection helper */}
              {(customStart || customEnd) && (
                <div className="mb-2 p-3 bg-primary/5 rounded-lg">
                  <div className="text-sm text-primary">
                    {customStart && !customEnd && 'Select end date'}
                    {customStart && customEnd && `Selected: ${formatDateRange(customStart, customEnd)}`}
                  </div>
                </div>
              )}

              {/* Compare option */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={compareEnabled}
                    onChange={(e) => setCompareEnabled(e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Compare to previous period</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex flex-row justify-end gap-2 mt-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  className="px-5 py-2 bg-primary text-primary-foreground text-base font-bold rounded-lg hover:bg-primary/90 transition-colors shadow-lg border-2 border-primary focus:outline-none focus:ring-2 focus:ring-primary/60"
                >
                  Apply
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TimeRangeFilter;